import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
} from '@angular/material/dialog';
import moment from 'moment';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { DateWithTimeSelectorComponent } from 'src/app/shared/components/date-with-time-selector/date-with-time-selector.component';
import { AppInvitationStatus } from 'src/app/shared/enums/app-invitation-status.enum';
import { ChargingUserService } from 'src/app/shared/services/charging-user.service';
import { SnackbarService, FeedbackInfoComponent } from 'ok-angular-shared/components';
import { ChargingUser, ChargingUserAppInvitation, ScheduleAppInvitationRequest } from '../charging-user.model';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { ErrorMessage } from 'src/app/shared/models/errorMessage.model';
import { CountryCodeSelectorComponent } from 'src/app/shared/components/country-code-selector/country-code-selector.component';
import { PhoneNumberInputComponent } from 'src/app/shared/components/phone-number-input/phone-number-input.component';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { DATE_FORMATS } from 'src/app/shared/date-format';
import { AsyncPipe } from '@angular/common';
interface ChargingUserSendSmsModel {
  phoneNumber: FormControl<string>;
  sendSmsLater: FormControl<boolean>;
  smsSendDate: FormControl<Date>;
  countryCode: FormControl<number>;
}

@Component({
  selector: 'adm-send-new-invitation',
  imports: [
    DateWithTimeSelectorComponent,
    CountryCodeSelectorComponent,
    PhoneNumberInputComponent,
    FeedbackInfoComponent,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatIcon,
    MatButton,
    MatSlideToggle,
    AsyncPipe,
    ReactiveFormsModule,
  ],
  providers: [{ provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS }],
  templateUrl: './send-new-invitation.component.html',
  styleUrl: './send-new-invitation.component.scss',
})
export class SendNewChargingUserInvitationComponent {
  dialog = inject(MatDialog);
  data = inject<{
    chargingUser: ChargingUser;
    appInvitation: null | ChargingUserAppInvitation;
  }>(MAT_DIALOG_DATA);
  chargingUserService = inject(ChargingUserService);
  private snackbarService = inject(SnackbarService);
  dialogRef = inject<MatDialogRef<SendNewChargingUserInvitationComponent>>(MatDialogRef);

  public sending$ = new BehaviorSubject<boolean>(false);
  public sendSmsFormGroup: FormGroup<ChargingUserSendSmsModel> = new FormGroup<ChargingUserSendSmsModel>({
    phoneNumber: new FormControl<string>('', { validators: [Validators.required], nonNullable: true }),
    sendSmsLater: new FormControl<boolean>(false, { nonNullable: true }),
    smsSendDate: new FormControl<Date>(moment().add(1, 'hour').toDate(), { nonNullable: true }),
    countryCode: new FormControl<number>(45, { validators: [Validators.required], nonNullable: true }),
  });
  constructor() {
    if (this.data.appInvitation != null) {
      this.sendSmsFormGroup.controls.phoneNumber.setValue(this.data.appInvitation.phoneNumber ?? '');
      this.sendSmsFormGroup.controls.countryCode.setValue(this.data.appInvitation.phoneCountryCode ?? 45);
    } else {
      this.sendSmsFormGroup.controls.phoneNumber.setValue(this.data.chargingUser.phoneNumber ?? '');
      this.sendSmsFormGroup.controls.countryCode.setValue(this.data.chargingUser.phoneCountryCode ?? 45);
    }
  }

  scheduleAppInvitation() {
    if (this.sending$.value) {
      return;
    }
    this.sending$.next(true);
    if (this.sendSmsFormGroup.valid) {
      const formValue = this.sendSmsFormGroup.getRawValue();
      firstValueFrom(
        this.chargingUserService.scheduleAppInvitation(this.data.chargingUser.id, <ScheduleAppInvitationRequest>{
          PhoneNumber: formValue.phoneNumber,
          ExecutionDate: formValue.smsSendDate,
          phoneCountryCode: formValue.countryCode,
        })
      )
        .then(_ => {
          this.snackbarService.success('Invitation planlagt');
          this.dialogRef.close();
          this.sending$.next(false);
        })
        .catch((httpError: HttpErrorResponse) => {
          if (httpError.status == 400 && httpError.error) {
            const error = httpError.error as ErrorMessage;
            switch (error.code) {
              case 500021:
                this.snackbarService.error(
                  `Der er allerede afsendt en invitation til dette nummer og der kan derfor ikke planlægges en afsendelse.`
                );
                break;
              case 101100:
                this.snackbarService.error(`Invitationen mangler et branch.io deeplink, så sms'en blev ikke afsendt.`);
                break;
              case 101101:
                this.snackbarService.error(`Invitationen mangler et branch.io deeplink, så sms'en blev ikke afsendt.`);
                break;
              default:
                this.snackbarService.error('Fejl ved planlægning af invitation');
                break;
            }
          }

          this.sending$.next(false);
        });
    }
  }

  sendSms() {
    if (!this.sendSmsFormGroup.controls.sendSmsLater.value) {
      this.sendAppInvitation();
      return;
    }
    this.scheduleAppInvitation();
  }

  sendAppInvitation() {
    if (this.sending$.value) {
      return;
    }
    if (this.sendSmsFormGroup.valid) {
      const formValue = this.sendSmsFormGroup.getRawValue();
      this.sending$.next(true);
      this.chargingUserService.sendAppInvitation(this.data.chargingUser.id, formValue.phoneNumber, formValue.countryCode).subscribe({
        next: (result: ChargingUserAppInvitation) => {
          if (result) {
            if (
              result.appInvitationStatus === AppInvitationStatus.SendError ||
              result.appInvitationStatus === AppInvitationStatus.NotSent
            ) {
              this.snackbarService.warning('Invitationen blev oprettet, men der skete en fejl ved sms afsendelse af invitationen');
            } else {
              this.snackbarService.success('Invitation sendt');
            }
            this.dialogRef.close();
          } else {
            this.snackbarService.error('Fejl ved afsendelse af invitation');
          }
          this.sending$.next(false);
        },
        error: (httpError: HttpErrorResponse) => {
          if (httpError.status == HttpStatusCode.BadRequest) {
            switch (httpError.error?.code) {
              case 500022:
                this.snackbarService.error(`Invitationen mangler et branch.io deeplink, så sms'en blev ikke afsendt.`);
                break;
              case 101100:
                this.snackbarService.error('Mobilnummeret angivet er ikke et gyldigt telefonnummer for den indtastede landekode.');
                break;
              case 101101:
                this.snackbarService.error('Mobilnummeret er af et ugyldigt format for den indtastede landekode.');
                break;
              default:
                this.snackbarService.error(httpError.error?.message);
                break;
            }
          } else {
            this.snackbarService.error(`Der er sket en ukendt fejl: (${httpError.error?.message})`);
          }

          this.sending$.next(false);
        },
      });
    }
  }
}
